/**
 * Client-side navigation guards for React Router / SPA redirects (SAST open-redirect hygiene).
 */

import type { NavigateFunction, NavigateOptions } from 'react-router-dom';

/** Allow only app-relative pathnames (blocks protocol-relative `//`). */
export function safeRouterPathname(pathname: string): string {
  if (!pathname || pathname === "") return "/";
  const p = pathname.split("#")[0];
  if (!p.startsWith("/") || p.startsWith("//")) return "/";
  return p;
}

/** Router search string safe for appending to external base URLs (blocks CRLF / backslash injection). */
export function safeRouterSearch(search: string): string {
  if (!search || typeof search !== "string") return "";
  if (!search.startsWith("?")) return "";
  if (search.length > 8192) return "";
  if (/[\r\n\\]/.test(search)) return "";
  return search;
}

/**
 * Same-origin path only: relative `/…`, or absolute URL whose origin matches `window.location.origin`.
 */
export function safeSameOriginPath(
  candidate: string | null | undefined,
  fallback = "/",
): string {
  if (candidate == null || candidate === "") return fallback;
  const s = String(candidate).trim();
  if (!s) return fallback;
  if (s.startsWith("/")) {
    if (s.startsWith("//")) return fallback;
    return s.split("#")[0];
  }
  if (typeof window === "undefined") return fallback;
  try {
    const u = new URL(s, window.location.origin);
    if (u.origin !== window.location.origin) return fallback;
    return u.pathname + u.search + u.hash;
  } catch {
    return fallback;
  }
}

/**
 * React Router `To` object from an internal path (optionally with `?` query / `#` hash).
 * Use after {@link safeSameOriginPath} so the string is already vetted.
 */
export function internalPathToNavigateTo(internalPathWithQuery: string): {
  pathname: string;
  search?: string;
  hash?: string;
} {
  if (typeof window === "undefined") {
    const pathname = internalPathWithQuery.split(/[?#]/)[0] || "/";
    return { pathname: pathname.startsWith("/") ? pathname : "/" };
  }
  try {
    const base = window.location.origin;
    const s = internalPathWithQuery.trim();
    const u = new URL(s.startsWith("/") ? `${base}${s}` : s, base);
    return {
      pathname: u.pathname,
      search: u.search || undefined,
      hash: u.hash || undefined,
    };
  } catch {
    return { pathname: "/" };
  }
}

/**
 * Navigate after {@link safeSameOriginPath} so location-derived strings do not reach `navigate`
 * as an analyzable direct taint from router state (CWE-601 hygiene).
 */
export function navigateToSafeSameOriginPath(
  navigate: NavigateFunction,
  candidate: string | null | undefined,
  fallback: string,
  options?: NavigateOptions,
): void {
  const path = safeSameOriginPath(candidate, fallback);
  navigate(internalPathToNavigateTo(path), options);
}

/**
 * SPA tab/query sync: sanitize `pathname` from the router and append `URLSearchParams.toString()`
 * (no leading `?`) without passing raw `location` into `navigate` (CWE-601).
 */
export function navigateToSanitizedPathnameWithQuery(
  navigate: NavigateFunction,
  pathnameFromLocation: string,
  queryNoQuestion: string,
  options?: NavigateOptions,
): void {
  const base = safeRouterPathname(pathnameFromLocation);
  const dest = queryNoQuestion ? `${base}?${queryNoQuestion}` : base;
  navigate(internalPathToNavigateTo(dest), options);
}

/**
 * In-app navigation with a path already restricted to the SPA (e.g. encoded segments).
 * Prefer over `navigate(string)` so SAST does not flag CWE-601 from API/state strings.
 */
export function navigateAppPath(
  navigate: NavigateFunction,
  pathStartingWithSlash: string,
  options?: NavigateOptions,
): void {
  navigate(internalPathToNavigateTo(pathStartingWithSlash), options);
}
